import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card, CardBody } from '../components/ui/Card';
import { MapPin, Search, CheckCircle, XCircle } from 'lucide-react';
export function ServiceArea() {
  const [zipCode, setZipCode] = useState('');
  const [searchResult, setSearchResult] = useState<'idle' | 'found' | 'not-found'>('idle');
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (zipCode.trim() === '') return;
    // Simulate zip code check
    // In a real app, this would check against an array of valid zip codes
    if (zipCode.startsWith('20') || zipCode.startsWith('21') || zipCode.startsWith('22')) {
      setSearchResult('found');
    } else {
      setSearchResult('not-found');
    }
  };
  const counties = ['Washington D.C.', 'Montgomery County, MD', "Prince George's County, MD", 'Fairfax County, VA', 'Arlington County, VA', 'Alexandria City, VA'];
  const majorCities = ['Washington D.C.', 'Silver Spring', 'Bethesda', 'Rockville', 'College Park', 'Bowie', 'Alexandria', 'Arlington'];
  return <div className="bg-white min-h-screen pb-20" data-id="element-1020">
      {/* Header */}
      <div className="bg-primary-900 text-white py-16" data-id="element-1021">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1022">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-1023">
            Our Service Area
          </h1>
          <p className="text-xl text-primary-100 max-w-3xl mx-auto" data-id="element-1024">
            Gozie Shipping and Transportation proudly serves the Washington D.C.
            Metro Area, Maryland, and Northern Virginia. Whether you need
            handicap-accessible transportation, school runs, or international
            shipping to Lagos, Nigeria — we are here to help.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-1025">
        <div className="flex flex-col lg:flex-row gap-12" data-id="element-1026">
          {/* Map and Search Area */}
          <div className="lg:w-2/3 space-y-8" data-id="element-1027">
            {/* Map Placeholder */}
            <div className="bg-warm-100 rounded-2xl overflow-hidden shadow-lg aspect-[4/3] relative flex items-center justify-center border border-warm-200" data-id="element-1028">
              <div className="text-center p-8" data-id="element-1029">
                <MapPin className="w-16 h-16 text-primary-400 mx-auto mb-4 opacity-50" data-id="element-1030" />
                <p className="text-warm-500 font-medium text-lg" data-id="element-1031">
                  [Interactive Map Integration]
                </p>
                <p className="text-warm-400 text-sm mt-2" data-id="element-1032">
                  Shows highlighted coverage zones
                </p>
              </div>
            </div>

            {/* Zip Code Checker */}
            <Card className="bg-primary-50 border-primary-200" data-id="element-1033">
              <CardBody className="p-8" data-id="element-1034">
                <h2 className="text-2xl font-bold text-primary-900 mb-2" data-id="element-1035">
                  Check Your Zip Code
                </h2>
                <p className="text-primary-700 mb-6" data-id="element-1036">
                  Enter your pickup zip code to see if you are within our
                  primary service area.
                </p>

                <form onSubmit={handleSearch} className="flex gap-4 max-w-md" data-id="element-1037">
                  <div className="flex-grow" data-id="element-1038">
                    <Input label="" placeholder="Enter 5-digit Zip Code" value={zipCode} onChange={e => setZipCode(e.target.value)} maxLength={5} className="mb-0" data-id="element-1039" />
                  </div>
                  <Button type="submit" className="mt-1" data-id="element-1040">
                    <Search className="w-5 h-5 mr-2" data-id="element-1041" />
                    Check
                  </Button>
                </form>

                {searchResult === 'found' && <div className="mt-6 p-4 bg-green-100 border border-green-300 rounded-lg flex items-start" data-id="element-1042">
                    <CheckCircle className="w-6 h-6 text-green-600 mr-3 flex-shrink-0" data-id="element-1043" />
                    <div data-id="element-1044">
                      <h3 className="text-green-800 font-bold" data-id="element-1045">
                        Great news! We serve your area.
                      </h3>
                      <p className="text-green-700 text-sm mt-1" data-id="element-1046">
                        You are within our primary coverage zone for affordable
                        passenger transport and mobility equipment shipping.
                      </p>
                      <Link to="/booking" className="inline-block mt-3 text-sm font-bold text-green-800 hover:underline" data-id="element-1047">
                        Book Now &rarr;
                      </Link>
                    </div>
                  </div>}

                {searchResult === 'not-found' && <div className="mt-6 p-4 bg-yellow-50 border border-yellow-300 rounded-lg flex items-start" data-id="element-1048">
                    <XCircle className="w-6 h-6 text-yellow-600 mr-3 flex-shrink-0" data-id="element-1049" />
                    <div data-id="element-1050">
                      <h3 className="text-yellow-800 font-bold" data-id="element-1051">
                        Outside Primary Area
                      </h3>
                      <p className="text-yellow-700 text-sm mt-1" data-id="element-1052">
                        This zip code is outside our standard coverage zone.
                        However, we often accommodate long-distance trips or
                        out-of-network requests for an additional mileage fee.
                      </p>
                      <a href="tel:2404132157" className="inline-block mt-3 text-sm font-bold text-yellow-800 hover:underline" data-id="element-1053">
                        Call us to discuss options &rarr;
                      </a>
                    </div>
                  </div>}
              </CardBody>
            </Card>
          </div>

          {/* Lists Area */}
          <div className="lg:w-1/3 space-y-8" data-id="element-1054">
            <div data-id="element-1055">
              <h2 className="text-2xl font-bold text-warm-900 mb-6 border-b border-warm-200 pb-2" data-id="element-1056">
                Areas Served
              </h2>
              <ul className="space-y-3" data-id="element-1057">
                {counties.map((county, i) => <li key={i} className="flex items-center text-warm-700 font-medium" data-id="element-1058">
                    <MapPin className="w-5 h-5 text-primary-500 mr-3" data-id="element-1059" />
                    {county}
                  </li>)}
              </ul>
            </div>

            <div data-id="element-1060">
              <h2 className="text-2xl font-bold text-warm-900 mb-6 border-b border-warm-200 pb-2" data-id="element-1061">
                Major Cities
              </h2>
              <div className="grid grid-cols-2 gap-y-3 gap-x-4" data-id="element-1062">
                {majorCities.map((city, i) => <div key={i} className="text-warm-700" data-id="element-1063">
                    {city}
                  </div>)}
              </div>
            </div>

            <Card className="bg-warm-900 text-white border-none mt-8" data-id="element-1064">
              <CardBody className="p-6" data-id="element-1065">
                <h3 className="text-xl font-bold mb-3" data-id="element-1066">
                  Long Distance Transport
                </h3>
                <p className="text-warm-300 text-sm mb-4" data-id="element-1067">
                  Need to travel across the state or to a specialized medical
                  facility outside our normal area? We provide long-distance
                  NEMT services by special arrangement.
                </p>
                <Link to="/contact" data-id="element-1068">
                  <Button variant="outline" className="w-full border-warm-500 text-white hover:bg-warm-800" data-id="element-1069">
                    Request Custom Quote
                  </Button>
                </Link>
              </CardBody>
            </Card>
          </div>
        </div>
      </div>
    </div>;
}